import React, { useState, useEffect } from "react";
import api from "../utils/axiosConfig";
import { toast } from "react-toastify";

interface HearingFormProps {
  setParentState: any;
  parentState: any;
}

interface State {
  id: number;
  slug: string | null;
}

interface City {
  id: number;
  name: string | null;
  zones: { id: number; name: string }[];
}

interface Zone {
  id: number;
  name: string | null;
}

const apiUri = import.meta.env.VITE_API_URI as string;
const emptyLocation = {
  state: null,
  city: null,
  zone: null,
  cityOptions: [],
  zoneOptions: [],
};

const HearingForm: React.FC<HearingFormProps> = ({
  setParentState,
  parentState,
}) => {
  const [locationFields, setLocationFields] = useState<
    Array<{
      state: string | null;
      city: string | null;
      zone: string | null;
      cityOptions: City[];
      zoneOptions: Zone[];
    }>
  >([{ ...emptyLocation }]);
  const [states, setStates] = useState<State[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`${apiUri}address/states`)
      .then((response) => {
        setStates(response.data);
      })
      .catch((error) => {
        console.error("Error fetching states:", error);
      });
  }, []);

  const handleAvailabilityChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const { name, checked } = event.target;
    const newAvailability = parentState.availability;
    newAvailability[name] = checked;

    setParentState({ ...parentState, availability: newAvailability });
  };

  const validateForm = () => {
    const isAnyChecked = Object.values(parentState.availability).some(
      (value) => value
    );
    if (!isAnyChecked) {
      toast.error(
        "Você deve selecionar pelo menos uma opção de disponibilidade",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      return false;
    }
    setError("");
    return true;
  };

  const addLocationField = () => {
    if (locationFields.length < 20) {
      const newLocation = { ...emptyLocation };
      setLocationFields((prevData) => [...prevData, newLocation]);
    }
  };

  const removeLocationField = () => {
    if (locationFields.length > 1) {
      const indexToRemove = locationFields.length - 1;
      const cityIdToRemove = locationFields[indexToRemove].city;
      const zoneIdToRemove = locationFields[indexToRemove].zone;
      if (zoneIdToRemove && cityIdToRemove) {
        setParentState((prevState: any) => ({
          ...prevState,
          citiesCovered: prevState.citiesCovered.filter(
            (city: { id: string }) => city.id !== cityIdToRemove
          ),
          cityZonesCovered: prevState.cityZonesCovered.filter(
            (zone: { id: string }) => zone.id !== zoneIdToRemove
          ),
        }));
      } else if (cityIdToRemove) {
        setParentState((prevState: any) => ({
          ...prevState,
          citiesCovered: prevState.citiesCovered.filter(
            (city: { id: string }) => city.id !== cityIdToRemove
          ),
        }));
      }

      setLocationFields((prevData) =>
        prevData.filter((_, index) => index !== indexToRemove)
      );
    }
  };

  const handleStateChange = async (index: number, stateId: string) => {
    try {
      const cities = await api.get(`${apiUri}address/states/${stateId}/cities`);
      const currentLocationFields = locationFields[index];
      currentLocationFields.state = stateId;
      currentLocationFields.cityOptions = cities.data;
      setLocationFields((prevFields) =>
        prevFields.map((field, i) =>
          i === index ? currentLocationFields : field
        )
      );
    } catch (ex) {
      console.error("Error fetching cities:", ex);
    }
  };

  const handleCityChange = (index: number, cityId: string) => {
    const currentLocationFields = locationFields[index];
    currentLocationFields.city = cityId;
    currentLocationFields.zoneOptions =
      currentLocationFields.cityOptions.find(
        (city) => city.id.toString() === cityId
      )?.zones || [];
    setLocationFields((prevFields) =>
      prevFields.map((field, i) =>
        i === index ? currentLocationFields : field
      )
    );
    setParentState((prevState: any) => ({
      ...prevState,
      citiesCovered: [...prevState.citiesCovered, { id: cityId }],
    }));
  };

  const handleZoneChange = (index: number, zoneId: string) => {
    const currentLocationFields = locationFields[index];
    currentLocationFields.zone = zoneId;
    setLocationFields((prevFields) =>
      prevFields.map((field, i) =>
        i === index ? currentLocationFields : field
      )
    );
    setParentState((prevState: any) => ({
      ...prevState,
      cityZonesCovered: [...prevState.cityZonesCovered, { id: zoneId }],
    }));
  };

  return (
    <div>
      <div className="pb-2">
        <h2 className="text-lg font-semibold leading-7 text-preppoBlue text-center">
          Audiências
        </h2>
        <div className="flex flex-col mb-2 mt-2 sm:mb-6 sm:mt-6">
          <label className="block text-sm font-medium leading-6 text-preppoBlue">
            Forma de Atendimento*
          </label>
          <select
            onChange={({ target }) =>
              parentState.availabilityModality ||
              setParentState({
                ...parentState,
                ...{ availabilityModality: target.value },
              })
            }
            className="p-2 border border-gray-300 rounded max-w-min text-sm"
            placeholder={parentState.availabilityModality}
            required
          >
            <option value="3">Ambos</option>
            <option value="1">Presencial</option>
            <option value="2">Online</option>
          </select>
        </div>
        <div>
          {locationFields.map((field, index) => (
            <div key={index} className="flex flex-col sm:flex-row gap-2 sm:gap-x-5">
              <div className="flex flex-col mb-2 mt-2 sm:mb-6 sm:mt-6 w-full sm:w-1/3">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Estados*
                </label>
                <select
                  onChange={(e) => handleStateChange(index, e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                  value={field.state || ""}
                  required
                >
                  <option value="">Selecione um estado</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.slug}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col mb-2 mt-2 sm:mb-6 sm:mt-6 w-full sm:w-1/3">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Cidades*
                </label>
                <select
                  onChange={(e) => handleCityChange(index, e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                  value={field.city || ""}
                  disabled={!field.state}
                  required
                >
                  <option value="">Selecione uma cidade</option>
                  {field.cityOptions.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col mb-2 mt-2 sm:mb-6 sm:mt-6 w-full sm:w-1/3">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Zonas
                </label>
                <select
                  onChange={(e) => handleZoneChange(index, e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                  value={field.zone || ""}
                  disabled={
                    !field.city ||
                    (!!field.city && field.zoneOptions.length === 0)
                  }
                >
                  <option value="">Selecione uma zona</option>
                  {field.zoneOptions.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-2 sm:space-x-4">
          {locationFields.length > 1 && (
            <button
              type="button"
              onClick={() => removeLocationField()}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-3 rounded-lg text-xs transition-all"
            >
              Selecionar menos
            </button>
          )}
          {locationFields.length < 50 && (
            <button
              type="button"
              onClick={addLocationField}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-3 rounded-lg text-xs transition-all"
            >
              Selecionar mais
            </button>
          )}
        </div>

        <div className="flex flex-col mb-2 mt-2 sm:mb-6 sm:mt-6">
          <label className="block text-sm font-medium leading-6 text-preppoBlue">
            Disponibilidade de Atendimento*
          </label>
          <div className="overflow-x-auto">
            <table
              onChange={validateForm}
              className="min-w-full bg-preppoBlue-light border border-gray-300"
            >
              <tbody>
                {[
                  {
                    day: "Segunda-feira",
                    morning: "mondayMorning",
                    afternoon: "mondayAfternoon",
                  },
                  {
                    day: "Terça-feira",
                    morning: "tuesdayMorning",
                    afternoon: "tuesdayAfternoon",
                  },
                  {
                    day: "Quarta-feira",
                    morning: "wednesdayMorning",
                    afternoon: "wednesdayAfternoon",
                  },
                  {
                    day: "Quinta-feira",
                    morning: "thursdayMorning",
                    afternoon: "thursdayAfternoon",
                  },
                  {
                    day: "Sexta-feira",
                    morning: "fridayMorning",
                    afternoon: "fridayAfternoon",
                  },
                ].map(({ day, morning, afternoon }) => (
                  <tr key={day}>
                    <td className="px-4 py-2 border-b text-preppoBlue text-sm">
                      {day}
                    </td>
                    <td className="px-4 py-2 border-b">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={
                            JSON.parse(parentState?.availability[morning]) ||
                            false
                          }
                          onChange={handleAvailabilityChange}
                          className="form-checkbox text-blue-600"
                          name={morning}
                        />
                        <span className="text-preppoBlue text-sm">Manhã</span>
                      </label>
                    </td>
                    <td className="px-4 py-2 border-b">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={
                            JSON.parse(parentState?.availability[afternoon]) ||
                            false
                          }
                          onChange={handleAvailabilityChange}
                          name={afternoon}
                          className="form-checkbox text-blue-600"
                        />
                        <span className="text-preppoBlue text-sm">Tarde</span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
        <div className="flex flex-col mt-6 gap-2">
          <label className="block text-sm font-medium leading-6 text-preppoBlue">
            Requisitos Mínimos*
          </label>
          <div className="flex flex-col mb-6 gap-2">
            <div className="p-6 bg-white rounded-lg shadow-md space-y-4">
              <div>
                <h3 className="text-s font-semibold text-preppoBlue mb-2">
                  Estrutura Tecnológica
                </h3>
                <p className="text-xs text-preppoBlue">
                  Celular e computador com câmera, microfone e conexão à
                  internet.
                </p>
                <p className="text-xs text-preppoBlue">
                  Conhecimento e acesso às plataformas mais usadas em audiências
                  virtuais, como Zoom, Microsoft Teams, CISCO Webex e Google
                  Meet.
                </p>
                <p className="text-xs text-preppoBlue">
                  Acesso a e-mail e ao aplicativo WhatsApp.
                </p>
              </div>
              <div>
                <h3 className="text-s font-semibold text-preppoBlue mb-2">
                  Estrutura Física
                </h3>
                <p className="text-xs text-preppoBlue">
                  No dia da audiência, estar em um ambiente silencioso, livre de
                  interferências externas e com boa iluminação.
                </p>
              </div>
              <div>
                <h3 className="text-s font-semibold text-preppoBlue mb-2">
                  Disponibilidade
                </h3>
                <p className="text-xs text-preppoBlue">
                  Estar disponível para participar de reuniões preparatórias de
                  alinhamento e orientações.
                </p>
              </div>
              <div>
                <h3 className="text-s font-semibold text-preppoBlue mb-2">
                  Audiências Presenciais
                </h3>
                <p className="text-xs text-preppoBlue">
                  Respeitar os horários e locais de encontro combinados
                  previamente.
                </p>
                <p className="text-xs text-preppoBlue">
                  Planejar o transporte, seja com meio de condução próprio,
                  transporte público ou carros de aplicativo, para chegar com
                  antecedência ao local da audiência e evitar atrasos por
                  imprevistos.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-2 w-full">
            <label className="flex items-start sm:items-center gap-2 sm:gap-3 text-sm font-medium leading-6 text-preppoBlue cursor-pointer">
              <input
                type="checkbox"
                name="agreeToTechnicalRequirements"
                className="mt-1 sm:mt-0 h-5 w-5 text-preppoBlue flex-shrink-0"
                onChange={({ target }) =>
                  setParentState({
                    ...parentState,
                    agreeToTechnicalRequirements: target.checked,
                  })
                }
                checked={parentState.agreeToTechnicalRequirements}
                required
              />
              <span className="text-sm leading-relaxed">
                Concordo com os requisitos mínimos*
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HearingForm;
